import React, { useState, useEffect } from 'react';
import { UserCircle } from 'lucide-react';
import ContactManager from '../Common/ContactManager';
import FormModalLayout from '../Common/FormModalLayout';
import Can from '../Common/Can';
import { identityService, contactService } from '../../services/api';
import { P } from '../../constants/permissions';
import toast from 'react-hot-toast';
import { userFacingError } from '../../i18n/userFacingError';
import { formatCpf } from '../../utils/cpf';
import { formatCnpj } from '../../utils/cnpj';
import { useTenant } from '../../contexts/TenantContext';
import { partyNoun } from '../../utils/tenantProfile';

const ClientModal = ({ client, onSubmit, onClose, zIndexClass = 'z-50' }) => {
  const { tenant } = useTenant();
  const party = partyNoun(tenant);
  const [formData, setFormData] = useState({
    name: '',
    cpf: '',
    cnpj: '',
    type: 'INDIVIDUAL',
    status: 'ACTIVE',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    ctf: '',
    email: '',
  });

  const [contacts, setContacts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState('');

  useEffect(() => {
    if (client) {
      // A API devolve `client_type`/`status` em minúsculas; o formulário usa maiúsculas.
      const rawType = String(client.type || client.client_type || '').toUpperCase();
      const type = rawType === 'LEGAL_ENTITY' || client.company_id ? 'LEGAL_ENTITY' : 'INDIVIDUAL';
      const rawStatus = String(client.status || '').toUpperCase();
      setFormData({
        name: client.name || '',
        cpf: client.cpf || '',
        cnpj: client.cnpj || '',
        type,
        status: rawStatus === 'INACTIVE' || client.is_active === false ? 'INACTIVE' : 'ACTIVE',
        address: client.address || '',
        city: client.city || '',
        state: client.state || '',
        zipCode: client.zipCode || '',
        ctf: client.ctf || '',
        email: client.email || '',
      });
      setContacts(client.contacts || []);
    }
  }, [client]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (client) {
        const partyPayload = {
          name: formData.name.trim() || undefined,
          email: formData.email.trim() || undefined,
          address: formData.address.trim() || undefined,
          city: formData.city.trim() || undefined,
        };
        if (/^[A-Za-z]{2}$/.test(formData.state.trim())) {
          partyPayload.state = formData.state.trim().toUpperCase();
        }
        if (/^\d{5}-?\d{3}$/.test(formData.zipCode.trim())) {
          partyPayload.zip_code = formData.zipCode.trim();
        }
        if (client.person_id) await identityService.updatePerson(client.person_id, partyPayload);
        if (client.company_id) await identityService.updateCompany(client.company_id, partyPayload);
        const payload = {
          client_type: formData.type === 'INDIVIDUAL' ? 'individual' : 'legal_entity',
          status: formData.status === 'ACTIVE' ? 'active' : 'inactive',
          ctf: formData.ctf.trim() || null,
        };
        if (formData.email.trim()) payload.email = formData.email.trim();
        onSubmit(payload);
        return;
      }
      const email = (formData.email || '').trim();
      if (!email) {
        toast.error('Informe o e-mail para criar o acesso ao sistema.');
        return;
      }

      let personId = null;
      let companyId = null;
      let createdAccess = false;

      if (formData.type === 'INDIVIDUAL') {
        const cpf = formatCpf(formData.cpf);
        if (!cpf) {
          toast.error('Informe um CPF válido com 11 dígitos.');
          return;
        }
        setCurrentStep('Criando pessoa e acesso...');
        const personData = {
          name: formData.name,
          cpf,
          email,
          address: formData.address || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          zip_code: formData.zipCode || undefined,
        };
        try {
          const personResponse = await identityService.createPersonWithAccessInvite({
            person: personData,
            invite_email: email,
            login_with: 'email',
          });
          personId = personResponse.data?.person?.id;
          createdAccess = true;
        } catch (error) {
          const detail = String(error.response?.data?.detail || '');
          if (error.response?.status === 400 && detail.includes('CPF already exists')) {
            const existingPerson = await identityService.getPersonByCpf(cpf);
            if (existingPerson?.data?.id) {
              personId = existingPerson.data.id;
            } else {
              throw new Error('CPF já existe mas pessoa não encontrada');
            }
          } else {
            const created = await identityService.createPerson(personData);
            personId = created.data?.id;
            if (!personId) throw error;
          }
        }
      } else {
        const cnpj = formatCnpj(formData.cnpj);
        if (!cnpj) {
          toast.error('Informe um CNPJ válido com 14 dígitos.');
          return;
        }
        setCurrentStep('Criando empresa e acesso...');
        const companyData = {
          name: formData.name,
          cnpj,
          email,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zipCode,
        };
        try {
          const companyResponse = await identityService.createCompanyWithAccessInvite({
            company: companyData,
            invite_email: email,
            login_with: 'email',
          });
          companyId = companyResponse.data?.company?.id;
          createdAccess = true;
        } catch (error) {
          const detail = String(error.response?.data?.detail || '');
          if (error.response?.status === 400 && detail.includes('CNPJ already exists')) {
            const existingCompany = await identityService.getCompanyByCnpj(cnpj);
            if (existingCompany?.data?.id) {
              companyId = existingCompany.data.id;
            } else {
              throw new Error('CNPJ já existe mas empresa não encontrada');
            }
          } else {
            const created = await identityService.createCompany(companyData);
            companyId = created.data?.id;
            if (!companyId) throw error;
          }
        }
      }
      
      // 2. Criar o cliente vinculado à pessoa/empresa
      setCurrentStep('Criando cliente...');
      const clientData = {
        client_type: formData.type === 'INDIVIDUAL' ? 'individual' : 'legal_entity',
        client_status: formData.status === 'ACTIVE' ? 'active' : 'inactive',
        person_id: personId,
        company_id: companyId,
        ctf: formData.ctf.trim() || null,
      };
      
      if (createdAccess) {
        toast.success('Acesso criado. A senha provisória e o link de ativação foram enviados por e-mail.');
      }

      // 3. Criar contatos se houver
      if (contacts.length > 0) {
        setCurrentStep('Criando contatos...');
        console.log('Criando contatos:', contacts);
        for (const contact of contacts) {
          await contactService.createContact({
            ...contact,
            person_id: personId,
            company_id: companyId
          });
        }
      }
      
      setCurrentStep('Finalizando...');
      onSubmit(clientData);
    } catch (error) {
      toast.error(userFacingError(error, 'identity:clientCreateError'));
    } finally {
      setIsSubmitting(false);
      setCurrentStep('');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar campo não utilizado quando mudar o tipo
    if (name === 'type') {
      setFormData(prev => ({
        ...prev,
        cpf: value === 'LEGAL_ENTITY' ? '' : prev.cpf,
        cnpj: value === 'INDIVIDUAL' ? '' : prev.cnpj
      }));
    }
  };

  return (
    <FormModalLayout
      zIndexClass={zIndexClass}
      title={client ? `Editar ${party.one}` : `Novo ${party.one}`}
      historyEntityType="clients"
      historyEntityId={client?.id}
      subtitle={
        client
          ? `Atualize os dados do ${party.one} vinculado.`
          : party.consultoria
            ? 'Crie o cliente, o usuário de acesso e o vínculo no sistema.'
            : 'Crie o requerente, o usuário de acesso e o vínculo no sistema.'
      }
      headerIcon={UserCircle}
      onClose={onClose}
      size="4xl"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline" disabled={isSubmitting}>
            Cancelar
          </button>
          <Can any={[P.IDENTITY_ACCESS, P.LICENSE_LICENCAS]}>
            <button
              type="submit"
              form="client-modal-form"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processando…' : client ? 'Atualizar' : 'Criar'}
            </button>
          </Can>
        </>
      }
    >
      <form id="client-modal-form" onSubmit={handleSubmit} className="space-y-8">
        <div>
          <h3 className="form-section-title">Informações básicas</h3>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="form-group lg:col-span-2">
              <label htmlFor="cl-name" className="form-label form-label-required">
                {formData.type === 'INDIVIDUAL' ? 'Nome completo' : 'Razão social'}
              </label>
              <input
                id="cl-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="cl-type" className="form-label">
                Tipo
              </label>
              <select
                id="cl-type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="form-select"
              >
                <option value="INDIVIDUAL">Pessoa física</option>
                <option value="LEGAL_ENTITY">Pessoa jurídica</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="cl-email" className="form-label form-label-required">
                E-mail
              </label>
              <input
                id="cl-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                required={!client}
                placeholder="contato@provedor.com"
                autoComplete="off"
                disabled={!!client}
              />
              {!client && (
                <p className="mt-1 text-xs text-gray-500">
                  O acesso ao portal é criado automaticamente. A senha provisória e o link de ativação vão para este e-mail.
                </p>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="cl-status" className="form-label">
                Status
              </label>
              <select
                id="cl-status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-select"
              >
                <option value="ACTIVE">Ativo</option>
                <option value="INACTIVE">Inativo</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="cl-doc" className="form-label form-label-required">
                {formData.type === 'INDIVIDUAL' ? 'CPF' : 'CNPJ'}
              </label>
              <input
                id="cl-doc"
                type="text"
                name={formData.type === 'INDIVIDUAL' ? 'cpf' : 'cnpj'}
                value={formData.type === 'INDIVIDUAL' ? formData.cpf : formData.cnpj}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="cl-ctf" className="form-label">
                CTF / AAPP (IBAMA)
              </label>
              <input
                id="cl-ctf"
                type="text"
                name="ctf"
                value={formData.ctf}
                onChange={handleChange}
                className="form-input"
                maxLength={50}
                placeholder="Número do Cadastro Técnico Federal"
              />
              <p className="mt-1 text-xs text-gray-500">
                Informe o número. A validação no IBAMA não está integrada —{' '}
                <a
                  href="https://www.gov.br/ibama/pt-br/servicos/cadastro-tecnico-federal-ctf-app"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-700 hover:underline"
                >
                  consultar no site oficial
                </a>
                .
              </p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="form-section-title">Endereço</h3>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="form-group lg:col-span-2">
              <label htmlFor="cl-addr" className="form-label">
                Endereço
              </label>
              <input
                id="cl-addr"
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="cl-city" className="form-label">
                Cidade
              </label>
              <input
                id="cl-city"
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="cl-state" className="form-label">
                Estado
              </label>
              <input
                id="cl-state"
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="cl-cep" className="form-label">
                CEP
              </label>
              <input
                id="cl-cep"
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                className="form-input"
              />
            </div>
          </div>
        </div>

        <div>
          <h3 className="form-section-title mb-4">Contatos</h3>
          <ContactManager contacts={contacts} onChange={setContacts} />
        </div>

        {isSubmitting && (
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-3">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-secondary-600" />
              <span className="text-sm text-secondary-800">{currentStep}</span>
            </div>
          </div>
        )}
      </form>
    </FormModalLayout>
  );
};

export default ClientModal;